import { useState, useEffect, useCallback, useRef } from "react";
import { FLEX_COL_FILL } from "../../../../../themes/sbux/components";
import SbuxEnhancedTable from "../../../../../components/SbuxTable/SbuxEnhancedTable";
import SbuxSubTabs from "../../../../../components/SbuxTabs/SbuxSubTabs";
import ClosureMsgEditDialog from "../../common/ClosureMsgEditDialog";
import ClosureMsgDialog from "../../common/ClosureMsgDialog";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import { columns, filterColumns } from "../closureMsg/columns";
import ClosureMsgRow from "./ClosureMsgRow";
import ClosureMsgCustomFilter from "../../common/ClosureMsgCustomFilter";
import { closureMsgFilterItem } from "../../utils";
import { Box } from "@mui/material";
import { subOnUpdateClosureMessages } from "../../../../../graphql/api";
import SbuxStackSnackbar from "../../../../../components/SbuxStackSnackbar";
import {
  CLOSUREMSG_REFRESH_USER_MESSAGE,
  OPERATION,
} from "../../../../../constants/index";
import { logMessage } from "../../../../../utils/amplifyLogger";
import SbuxLoader from "../../../../../components/SbuxLoader";
import {
  updateCMData,
  createCMData,
  deleteCMData,
  resetHighlight,
} from "../../../../../slices/closureMsgSlice";
import { getInstanceIdSelector } from "../../../../../selectors/userInstanceSelector";
import { getCognitoUserIdSelector } from "../../../../../selectors/authSelector";
import { getClosureMsgStatusSelector } from "../../../../../selectors/closureMsgSelector";
import { useAppDispatch, useAppSelector } from "../../../../../store/types";

interface ClosureTab {
  value: string;
  name: string;
  [key: string]: unknown;
}

interface Language {
  code: string;
  name: string;
  [key: string]: unknown;
}

interface BusinessItem {
  id: string;
  value?: string;
  name: string;
  [key: string]: unknown;
}

interface ClosureMsgItem {
  id?: string;
  type?: string;
  status?: string;
  highLight?: boolean;
  [key: string]: unknown;
}

interface Props {
  closureTabs: ClosureTab[];
  languages: Language[];
  business: BusinessItem[];
  closureMsgData: ClosureMsgItem[];
  handleRefrehClick: () => void;
  filteredItem: string[];
  setFilteredItem: (items: string[]) => void;
  closureMsgReset: boolean;
  globalConfigUi: Record<string, unknown>;
}

const ClosureMsgTabs = ({
  closureTabs,
  languages,
  business,
  closureMsgData,
  handleRefrehClick,
  filteredItem,
  setFilteredItem,
  closureMsgReset,
  globalConfigUi,
}: Props) => {
  const updatedBusiness = business.map((cur) => ({
    ...cur,
    value: cur.id,
  }));
  const cognitoUserId = useAppSelector(getCognitoUserIdSelector);
  const instanceId = useAppSelector(getInstanceIdSelector);
  const closureMsgStatus = useAppSelector(getClosureMsgStatusSelector);
  const isLoading = closureMsgStatus === "pending";

  const [closureSubTabId, setClosureSubTabId] = useState<string>(
    closureTabs[0]?.value ?? "",
  );
  const [openAddNew, setOpenAddNew] = useState<boolean>(false);
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [closureMsgFilterData, setClosureMsgFilterData] = useState<
    ClosureMsgItem[]
  >([]);
  const [isCustomFilter, setIsCustomFilter] = useState<boolean>(false);
  const [row, setRow] = useState<ClosureMsgItem | null>(null);
  const [radioButtonValue, setRadioButtonValue] = useState<string | null>(null);
  const [customFromDate, setCustomFromDate] = useState<unknown>(null);
  const [customToDate, setCustomToDate] = useState<unknown>(null);
  const [updatedClosureId, setUpdatedClosureId] = useState<string | null>(null);
  const [openCMUpdateSnackbar, setOpenCMUpdateSnackbar] =
    useState<boolean>(false);

  // Ref to hold latest closureMsgData for stable handleClose callback
  const closureMsgDataRef = useRef<ClosureMsgItem[]>(closureMsgData);

  const parseMessage = (message: Record<string, unknown>) => {
    return {
      ...message,
      occurrences: JSON.parse(message?.occurrences as string),
      repeatPattern: JSON.parse(message?.repeatPattern as string),
    };
  };

  useEffect(() => {
    closureMsgDataRef.current = closureMsgData;
    setClosureMsgFilterData(closureMsgData);
    handleSubTabChange(closureSubTabId);
    setIsCustomFilter(false);
  }, [closureMsgData, filteredItem]);

  const subs: { unsubscribe: () => void }[] = [];
  const dispatch = useAppDispatch();

  const handleClosureSubscription = (data: {
    data?: { onProcessedClosureMessage?: Record<string, unknown> };
  }) => {
    const closureData = data?.data?.onProcessedClosureMessage;
    if (!closureData) return;

    const operation = closureData.__operation;
    const lastUpdatedBy = closureData?.lastUpdatedBy as string | undefined;

    // Determine if this update was made by the current user
    const isOwnUpdate =
      cognitoUserId && lastUpdatedBy && lastUpdatedBy === cognitoUserId;
    if (operation === OPERATION.DELETE || !isOwnUpdate) {
      setUpdatedClosureId((closureData?.id as string | null) ?? null);
      const parsedMessage = parseMessage(closureData);

      switch (operation) {
        case OPERATION.CREATE:
          dispatch(createCMData(parsedMessage));
          break;
        case OPERATION.UPDATE:
          dispatch(updateCMData(parsedMessage));
          break;
        case OPERATION.DELETE:
          dispatch(deleteCMData(parsedMessage));
          break;
      }

      if (!isOwnUpdate) {
        setOpenCMUpdateSnackbar(true);
      }
    }
  };

  useEffect(() => {
    subs.forEach((sub) => sub.unsubscribe());

    setTimeout(() => {
      const onProcessSub = subOnUpdateClosureMessages(
        instanceId ?? "",
      ).subscribe(handleClosureSubscription, (err) =>
        logMessage(
          `Closure Messages `,
          `Something went wrong in Subscription API => ${err}`,
          `error`,
        ),
      );

      subs.push(onProcessSub);
    }, 0);

    return () => {
      subs.forEach((sub) => sub.unsubscribe());
    };
  }, []);

  const handleAddNew = () => {
    setOpenAddNew((prevState) => !prevState);
  };

  const handleEditRowClick = (row: ClosureMsgItem) => {
    setRow(row);
    setIsEditOpen((prevState) => !prevState);
    setRadioButtonValue(null);
  };

  const handleRadioOkClick = (value: string) => {
    setRadioButtonValue(value);
  };

  const handleSubTabChange = (newValue: string) => {
    setClosureSubTabId(newValue);

    if (newValue === "custom") {
      customDateData(customFromDate, customToDate);
      setIsCustomFilter((prevState) => {
        return !prevState;
      });
    } else {
      const data = closureMsgFilterItem(
        newValue,
        closureMsgData,
        filteredItem,
        undefined,
        undefined,
      );
      setClosureMsgFilterData((data ?? []) as ClosureMsgItem[]);
    }
  };
  const handleCloseCustomFilter = () => {
    setIsCustomFilter(false);
  };

  const handleClose = useCallback(() => {
    setOpenCMUpdateSnackbar(false);
    const updatedData = closureMsgDataRef.current.map((item) => {
      return { ...item, highLight: false };
    });
    setClosureMsgFilterData(updatedData);
    dispatch(resetHighlight(updatedData));
  }, [dispatch]);

  const customDateData = (fromDate: unknown, toDate: unknown) => {
    const data = closureMsgFilterItem(
      closureSubTabId,
      closureMsgData,
      filteredItem,
      fromDate,
      toDate,
    );
    setCustomFromDate(fromDate);
    setCustomToDate(toDate);
    setClosureMsgFilterData((data ?? []) as ClosureMsgItem[]);
    setIsCustomFilter(false);
  };
  const handleCustomOkClick = (fromDate: unknown, toDate: unknown) => {
    customDateData(fromDate, toDate);
  };
  const handleSubTabClick = (tab: { value: string }) => {
    handleSubTabChange(tab.value);
  };
  const SubTabsComponent = () => {
    return (
      <SbuxSubTabs
        tabList={closureTabs}
        tabId={closureSubTabId}
        handleTabClick={handleSubTabClick}
      />
    );
  };

  return (
    <Box sx={FLEX_COL_FILL}>
      {isLoading ? (
        <SbuxLoader />
      ) : (
        <SbuxEnhancedTable
          rows={
            closureMsgReset && filteredItem.length === 0
              ? []
              : closureMsgFilterData
          }
          columns={columns}
          defaultSortingField={"startDate"}
          defaultSortOrder={"asc"}
          RowSource={
            ClosureMsgRow as Parameters<
              typeof SbuxEnhancedTable
            >[0]["RowSource"]
          }
          showFooter={true}
          showSearchBar={true}
          isManager={true}
          filterColumns={filterColumns}
          defaultFilterColumns={filteredItem}
          setFilteredItem={setFilteredItem}
          filterTitle={<SbuxTranslate>{"Closure Messages"}</SbuxTranslate>}
          showSubTabs
          showAddNewButton
          handleAddNewButtonClick={handleAddNew}
          addNewButtonTitle={<SbuxTranslate>{"Add New"}</SbuxTranslate>}
          SubTabs={SubTabsComponent}
          handleEditRowClick={
            handleEditRowClick as (row: Record<string, unknown>) => void
          }
          handleRefreshClick={handleRefrehClick}
          updatedRowId={updatedClosureId}
          isLoading={isLoading}
          footerMarginBottom={"0"}
        />
      )}
      {openAddNew && (
        <ClosureMsgDialog
          title={
            <SbuxTranslate>{`New Closure Message Configuration`}</SbuxTranslate>
          }
          open={openAddNew}
          toggleDialogOpen={handleAddNew}
          business={updatedBusiness}
          languages={
            languages as Parameters<typeof ClosureMsgDialog>[0]["languages"]
          }
          globalConfigUi={globalConfigUi}
          row={{}}
          isAddNew
        />
      )}
      {isCustomFilter && (
        <ClosureMsgCustomFilter
          title={<SbuxTranslate>{"Custom Filter"}</SbuxTranslate>}
          open={isCustomFilter}
          toggleOpen={handleCloseCustomFilter}
          handleCustomFilterOkClick={handleCustomOkClick}
        />
      )}
      {isEditOpen && row && row.type === "Single" ? (
        <ClosureMsgDialog
          title={
            <SbuxTranslate>{`Closure Message Configuration`}</SbuxTranslate>
          }
          open={isEditOpen}
          toggleDialogOpen={() => setIsEditOpen(false)}
          handleRadioOkClick={handleRadioOkClick}
          row={(row ?? {}) as Record<string, unknown>}
        />
      ) : (
        isEditOpen &&
        row &&
        row.type !== "Single" && (
          <ClosureMsgEditDialog
            title={
              <SbuxTranslate>{`Closure Message Configuration`}</SbuxTranslate>
            }
            open={isEditOpen}
            toggleDialogOpen={() => setIsEditOpen(false)}
            handleRadioOkClick={handleRadioOkClick}
            row={(row ?? {}) as Record<string, unknown>}
          />
        )
      )}
      {radioButtonValue && (
        <ClosureMsgDialog
          title={
            <SbuxTranslate>{`Closure Message Configuration`}</SbuxTranslate>
          }
          open={isEditOpen}
          toggleDialogOpen={() => setIsEditOpen(false)}
          row={(row ?? {}) as Record<string, unknown>}
          isAddNew={false}
          isSingleInstance={
            radioButtonValue === "singleInstance" ? true : false
          }
        />
      )}
      {openCMUpdateSnackbar && (
        <SbuxStackSnackbar
          width={380}
          open={openCMUpdateSnackbar}
          message={CLOSUREMSG_REFRESH_USER_MESSAGE}
          autoHide={false}
          handleClose={handleClose}
        />
      )}
    </Box>
  );
};

export default ClosureMsgTabs;
